import React, { forwardRef } from 'react';

interface TextInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'prefix'
> {
  label?: string;
  error?: string;
  leftSection?: React.ReactNode;
  prefix?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  height?: string;
  isRequired?: boolean;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      placeholder,
      type = 'text',
      id,
      isRequired,
      value,
      onChange,
      onBlur,
      error,
      name,
      leftSection,
      prefix,
      size = 'md',
      onFocus,
      autoFocus,
      ...restProps
    }: TextInputProps,
    ref
  ) => {
    const sizeMap = {
      sm: {
        wrapper: 'py-[6px]',
        input: 'py-[6px]',
        leftSection: 'px-2 py-[6px]',
        text: 'text-xs',
      },
      md: {
        wrapper: 'py-[10px]',
        input: 'py-[10px]',
        leftSection: 'px-3 py-[10px]',
        text: 'text-sm md:text-base lg:text-sm 3xl:text-base',
      },
      lg: {
        wrapper: 'py-[14px]',
        input: 'py-[14px]',
        leftSection: 'px-4 py-[14px]',
        text: 'text-base md:text-lg lg:text-base 3xl:text-lg',
      },
      xl: {
        wrapper: 'py-[18px]',
        input: 'py-[18px]',
        leftSection: 'px-5 py-[18px]',
        text: 'text-lg md:text-xl lg:text-lg 3xl:text-xl',
      },
    };

    const currentSize = sizeMap[size] || sizeMap.md;

    return (
      <div className="w-full flex flex-col items-start gap-1">
        <label
          htmlFor={id || name}
          className="text-left text-xs md:text-sm lg:text-sm 3xl:text-base text-input-active-label font-semibold"
        >
          {label}{' '}
          {isRequired && <span className="text-sm text-error-1">*</span>}
        </label>

        <div
          className={`w-full flex items-center rounded-md bg-modal-input transition-all ${error ? 'border-0.5 border-error-2' : 'border-0.5 border-gray-3/20'}`}
        >
          {leftSection && (
            <div
              className={`flex items-center justify-center ${currentSize.leftSection} border-r border-gray-3/20`}
            >
              {leftSection}
            </div>
          )}

          {prefix && (
            <span
              className={`pl-[14px] ${currentSize.text} text-input-inactive-placeholder`}
            >
              {prefix}
            </span>
          )}

          <input
            ref={ref}
            id={id || name}
            name={name}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            autoFocus={autoFocus}
            required={isRequired}
            className={`placeholder:text-input-inactive-placeholder placeholder:text-sm placeholder:3xl:text-base flex-1 rounded-md ${currentSize.input} ${prefix ? 'px-1' : 'px-[14px]'} ${leftSection && !prefix ? 'pl-[14px]' : ''} focus:outline-none ${currentSize.text} text-text-primary-highlight bg-transparent border-0`}
            {...restProps}
          />
        </div>

        {error && (
          <p className="text-error-2 text-xs md:text-xs mt-1">{error}</p>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
